import React, { useState, useEffect, useCallback } from 'react';
import { useNode } from '../contexts/NodeContext';
import { useAuth } from '../contexts/AuthContext';
import {
  BellAlertIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  XMarkIcon,
  CpuChipIcon,
  CircleStackIcon,
  ServerStackIcon,
  AdjustmentsHorizontalIcon,
  EnvelopeIcon,
  ArrowPathIcon,
  UserPlusIcon,
  ChatBubbleLeftRightIcon,
  ArrowUturnLeftIcon,
} from '@heroicons/react/24/outline';
import { notifyAlertCountsChanged } from '@trinetra/shared';
import api from '../services/api.service';

// ─── Types ─────────────────────────────────────────────────────

interface AlertRule {
  id: number;
  node_id: string;
  alert_type: string;
  enabled: boolean;
  warning_threshold: number;
  critical_threshold: number;
  consecutive_checks: number;
  cooldown_minutes: number;
  email_recipients: string[];
}

interface AlertEvent {
  id: number;
  node_id: string;
  node_name: string | null;
  alert_type: string;
  severity: string;
  value: number | null;
  threshold: number | null;
  message: string | null;
  active: boolean;
  acknowledged: boolean;
  acknowledged_by: string | null;
  acknowledged_by_name: string | null;
  acknowledged_at: string | null;
  assigned_to: string | null;
  assigned_to_name: string | null;
  assigned_by_name: string | null;
  resolved_at: string | null;
  resolved_by_name: string | null;
  cancelled: boolean;
  cancelled_by_name: string | null;
  cancelled_at: string | null;
  cancel_reason: string | null;
  created_at: string;
}

interface AlertSummary {
  unacknowledged_count: number;
  active_count: number;
  active_critical: number;
  active_warning: number;
}

// ─── Service error domain (separate from resource alerts + AI analytics) ──
interface ServiceError {
  id: number;
  node_id: string;
  node_name: string | null;
  service: string;
  error_code: string;
  component: string | null;
  severity: string;
  message: string;
  details: any;
  occurrence_count: number;
  active: boolean;
  acknowledged: boolean;
  acknowledged_by: string | null;
  acknowledged_by_name: string | null;
  assigned_to: string | null;
  assigned_to_name: string | null;
  assigned_by_name: string | null;
  resolved_by_name: string | null;
  last_seen: string;
  created_at: string;
}

interface CloudUser {
  id: string;
  name: string | null;
  email: string;
}

// Which system-alert domain a row belongs to (drives the API base path).
type AlertSource = 'service_error' | 'alert_event';
const SOURCE_BASE: Record<AlertSource, string> = {
  service_error: '/system-alerts/errors',
  alert_event: '/alert-system/events',
};

interface ServiceRollup {
  service: string;
  active: number;
  critical: number;
  last_seen: string;
}

// ─── Helpers ────────────────────────────────────────────────────

const alertTypeLabel: Record<string, string> = {
  cpu_high: 'CPU Usage',
  memory_high: 'Memory Usage',
  disk_high: 'Disk Usage',
  node_offline: 'Node Offline',
};

const alertTypeIcon: Record<string, React.ReactNode> = {
  cpu_high: <CpuChipIcon className="h-4 w-4" />,
  memory_high: <ServerStackIcon className="h-4 w-4" />,
  disk_high: <CircleStackIcon className="h-4 w-4" />,
  node_offline: <XMarkIcon className="h-4 w-4" />,
};

const sevColors: Record<string, string> = {
  critical: 'bg-red-500/15 text-red-500',
  error: 'bg-orange-500/15 text-orange-500',
  warning: 'bg-amber-500/15 text-amber-500',
};

function formatDateTime(v: string | null) {
  if (!v) return '—';
  const d = new Date(v);
  return d.toLocaleDateString([], { day: '2-digit', month: 'short' }) + ' ' + d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
}

// ─── Shared style tokens (match AlertInvestigationPage) ────────
const cardCls = 'rounded-xl border border-border bg-card shadow-card';
const selectCls = 'h-9 rounded-lg border border-border bg-background px-3 text-[13px] text-foreground focus:border-brand focus:outline-none';
const fieldCls = 'w-full h-9 rounded-lg border border-border bg-background px-3 text-[13px] text-foreground placeholder:text-muted-foreground focus:border-brand focus:outline-none';
const badgeCls = 'px-1.5 py-0.5 text-[10px] font-bold uppercase rounded';
const thCls = 'px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wider text-muted-foreground whitespace-nowrap';
const labelCls = 'block text-[12px] font-medium text-muted-foreground mb-1';
const primaryBtnCls = 'h-9 px-4 rounded-lg bg-brand text-white text-[13px] font-medium hover:bg-brand/90 disabled:opacity-50 transition-colors whitespace-nowrap';
const ghostBtnCls = 'h-9 px-4 rounded-lg border border-border text-[13px] text-foreground hover:bg-muted disabled:opacity-50 transition-colors whitespace-nowrap';

const SeverityBadge = ({ severity }: { severity: string }) => (
  <span className={`${badgeCls} ${sevColors[severity] || 'bg-gray-500/15 text-gray-500'}`}>{severity}</span>
);

const StatusBadge = ({ active, resolvedAt, cancelled }: { active: boolean; resolvedAt?: string | null; cancelled?: boolean }) =>
  active ? (
    <span className={`${badgeCls} bg-red-500/15 text-red-500 animate-pulse`}>Active</span>
  ) : cancelled ? (
    <span className={`${badgeCls} bg-muted text-muted-foreground`}>Cancelled</span>
  ) : resolvedAt !== undefined ? (
    <span className={`${badgeCls} bg-emerald-500/15 text-emerald-500`}>Resolved</span>
  ) : null;

// ═══════════════════════════════════════════════════════════════
//  MAIN PAGE COMPONENT
// ═══════════════════════════════════════════════════════════════

const TABS = [
  { key: 'events', label: 'Alert Events', icon: BellAlertIcon },
  { key: 'serviceErrors', label: 'Service Errors', icon: ServerStackIcon },
  { key: 'rules', label: 'Alert Rules', icon: AdjustmentsHorizontalIcon },
  { key: 'emailConfig', label: 'Email Config', icon: EnvelopeIcon },
] as const;

export default function SystemAlertsPage() {
  const [tab, setTab] = useState<'events' | 'serviceErrors' | 'rules' | 'emailConfig'>('events');
  const [summary, setSummary] = useState<AlertSummary | null>(null);
  const [events, setEvents] = useState<AlertEvent[]>([]);
  const [rules, setRules] = useState<AlertRule[]>([]);
  const [serviceErrors, setServiceErrors] = useState<ServiceError[]>([]);
  const [serviceRollup, setServiceRollup] = useState<ServiceRollup[]>([]);
  const [svcFilter, setSvcFilter] = useState<{ active?: boolean; severity?: string; service?: string }>({ active: true });
  const [loading, setLoading] = useState(true);
  const [eventsFilter, setEventsFilter] = useState<{ active?: boolean; severity?: string }>({ active: true });
  const [users, setUsers] = useState<CloudUser[]>([]);
  const [assignFor, setAssignFor] = useState<{ source: AlertSource; id: number; title: string } | null>(null);
  const [activityFor, setActivityFor] = useState<{ source: AlertSource; id: number; title: string } | null>(null);
  const [detailFor, setDetailFor] = useState<{ source: AlertSource; row: any; title: string } | null>(null);
  // Resolve / cancel of an alert event opens a small note/reason prompt first.
  const [eventActionFor, setEventActionFor] = useState<{ id: number; kind: 'resolve' | 'cancel' } | null>(null);
  const { nodes } = useNode();
  const { hasPermission } = useAuth();
  // Acknowledge / assign / resolve / cancel / reopen mutate service errors and
  // alert events; the backend gates them on system_alert.edit, so hide when absent.
  const canEdit = hasPermission('system_alert.edit');

  // Users for the assign dropdown; needs users.view — hide assign if forbidden.
  useEffect(() => {
    api.get('/users')
      .then((res) => setUsers(res.data?.data || []))
      .catch(() => setUsers([]));
  }, []);

  const fetchSummary = useCallback(async () => {
    try {
      const res = await api.get('/alert-system/summary');
      setSummary(res.data?.data || res.data);
    } catch { /* ignore */ }
  }, []);

  const fetchEvents = useCallback(async () => {
    try {
      const params: any = { limit: 100 };
      if (eventsFilter.active !== undefined) params.active = eventsFilter.active;
      if (eventsFilter.severity) params.severity = eventsFilter.severity;
      const res = await api.get('/alert-system/events', { params });
      const data = res.data?.data || res.data;
      setEvents(data.rows || data);
    } catch { /* ignore */ }
  }, [eventsFilter]);

  const fetchRules = useCallback(async () => {
    try {
      const res = await api.get('/alert-system/rules');
      setRules(res.data?.data || res.data);
    } catch { /* ignore */ }
  }, []);

  // ── Service errors (system/service domain) ──
  const fetchServiceErrors = useCallback(async () => {
    try {
      const params: any = { limit: 200 };
      if (svcFilter.active !== undefined) params.active = svcFilter.active;
      if (svcFilter.severity) params.severity = svcFilter.severity;
      if (svcFilter.service) params.service = svcFilter.service;
      const res = await api.get('/system-alerts/errors', { params });
      const data = res.data?.data || res.data;
      setServiceErrors(data.rows || data);
    } catch { /* ignore */ }
  }, [svcFilter]);

  const fetchServiceRollup = useCallback(async () => {
    try {
      const res = await api.get('/system-alerts/services');
      setServiceRollup(res.data?.data || res.data || []);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      await Promise.all([fetchSummary(), fetchEvents(), fetchRules(), fetchServiceErrors(), fetchServiceRollup()]);
      setLoading(false);
    };
    load();
  }, [fetchSummary, fetchEvents, fetchRules, fetchServiceErrors, fetchServiceRollup]);

  useEffect(() => { fetchEvents(); }, [eventsFilter, fetchEvents]);
  useEffect(() => { fetchServiceErrors(); }, [svcFilter, fetchServiceErrors]);

  // Poll for real-time updates every 15s
  useEffect(() => {
    const interval = setInterval(() => {
      fetchSummary();
      fetchEvents();
      fetchServiceErrors();
      fetchServiceRollup();
    }, 15000);
    return () => clearInterval(interval);
  }, [fetchSummary, fetchEvents, fetchServiceErrors, fetchServiceRollup]);

  const refreshAll = () => {
    fetchSummary();
    fetchEvents();
    fetchServiceErrors();
    fetchServiceRollup();
    fetchRules();
  };

  const handleAcknowledge = async (id: number) => {
    try {
      await api.patch(`/alert-system/events/${id}/acknowledge`);
      fetchEvents();
      fetchSummary();
    } catch { /* ignore */ }
  };

  const handleServiceAck = async (id: number) => {
    try {
      await api.patch(`/system-alerts/errors/${id}/acknowledge`);
      fetchServiceErrors();
      // The title bar counts UNACKNOWLEDGED service errors, so this changed it — say so
      // rather than leaving the badge on a stale number for up to a minute (alertCountsBus).
      notifyAlertCountsChanged();
    } catch { /* ignore */ }
  };

  const handleServiceResolve = async (id: number) => {
    try {
      await api.post(`/system-alerts/errors/${id}/resolve`);
      fetchServiceErrors();
      fetchServiceRollup();
      notifyAlertCountsChanged();
    } catch { /* ignore */ }
  };

  const handleServiceReopen = async (id: number) => {
    try {
      await api.post(`/system-alerts/errors/${id}/reopen`);
      fetchServiceErrors();
      fetchServiceRollup();
      notifyAlertCountsChanged();
    } catch { /* ignore */ }
  };

  const handleAssign = async (target: { source: AlertSource; id: number }, assignee: CloudUser) => {
    try {
      await api.patch(`${SOURCE_BASE[target.source]}/${target.id}/assign`, {
        assignee_id: assignee.id,
        assignee_name: assignee.name || assignee.email,
      });
      if (target.source === 'service_error') fetchServiceErrors(); else fetchEvents();
      notifyAlertCountsChanged();
    } catch { /* ignore */ }
  };

  const handleRuleUpdate = async (rule: AlertRule, field: string, value: any) => {
    try {
      await api.put(`/alert-system/rules/${rule.node_id}/${rule.alert_type}`, { [field]: value });
      fetchRules();
    } catch { /* ignore */ }
  };

  // Bulk-save drafted rule changes for a node (dirty-gated + confirmed in RulesPanel).
  const handleRulesSave = async (nodeId: string, rulesArr: any[]) => {
    await api.put(`/alert-system/rules/${nodeId}`, { rules: rulesArr });
    await fetchRules();
  };

  const handleResolveEvent = async (id: number, note: string) => {
    try {
      await api.post(`/alert-system/events/${id}/resolve`, { note });
      fetchEvents();
      fetchSummary();
    } catch { /* ignore */ }
  };

  const handleCancelEvent = async (id: number, reason: string) => {
    try {
      await api.post(`/alert-system/events/${id}/cancel`, { reason });
      fetchEvents();
      fetchSummary();
    } catch { /* ignore */ }
  };

  const svcUnacked = serviceErrors.filter((e) => e.active && !e.acknowledged).length;
  const tabBadge: Record<string, number> = {
    events: summary?.unacknowledged_count || 0,
    serviceErrors: svcUnacked,
  };

  if (loading) {
    return (
      <div className="space-y-4 p-3 sm:p-6">
        <div className="h-8 w-56 rounded-lg bg-muted animate-pulse" />
        <div className="h-10 rounded-lg bg-muted animate-pulse" />
        {[1, 2, 3, 4, 5].map((i) => (
          <div key={i} className="h-12 rounded-lg bg-muted animate-pulse" />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-4 p-3 sm:p-6">
      {/* ── Header ── */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <h1 className="text-xl font-semibold text-foreground">System Alerts</h1>
          <span className="hidden sm:inline text-[13px] text-muted-foreground">Resource alerts & per-service errors</span>
        </div>
        <div className="flex items-center gap-2">
          {summary && (
            <>
              {summary.active_critical > 0 && (
                <span className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[12px] font-medium bg-red-500/15 text-red-500">
                  <ExclamationTriangleIcon className="h-3.5 w-3.5" />
                  {summary.active_critical} Critical
                </span>
              )}
              {summary.active_warning > 0 && (
                <span className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[12px] font-medium bg-amber-500/15 text-amber-500">
                  <ExclamationTriangleIcon className="h-3.5 w-3.5" />
                  {summary.active_warning} Warning
                </span>
              )}
              {summary.active_count === 0 && (
                <span className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[12px] font-medium bg-emerald-500/15 text-emerald-500">
                  <CheckCircleIcon className="h-3.5 w-3.5" />
                  All Clear
                </span>
              )}
            </>
          )}
          <button
            onClick={refreshAll}
            className="flex h-8 w-8 items-center justify-center rounded-lg border border-border text-muted-foreground hover:bg-muted transition-colors"
            title="Refresh"
          >
            <ArrowPathIcon className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* ── Tabs ── */}
      <div className="flex items-center gap-1 border-b border-border overflow-x-auto">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`flex items-center gap-1.5 px-4 py-2 text-[13px] font-medium border-b-2 transition-colors whitespace-nowrap ${tab === t.key
              ? 'border-brand text-brand'
              : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
          >
            <t.icon className="h-4 w-4" />
            {t.label}
            {(tabBadge[t.key] || 0) > 0 && (
              <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-red-500 text-white">
                {tabBadge[t.key]}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* ── Tab Content ── */}
      {tab === 'events' ? (
        <EventsPanel
          events={events}
          filter={eventsFilter}
          setFilter={setEventsFilter}
          onAcknowledge={handleAcknowledge}
          onResolve={(e: AlertEvent) => setEventActionFor({ id: e.id, kind: 'resolve' })}
          onCancel={(e: AlertEvent) => setEventActionFor({ id: e.id, kind: 'cancel' })}
          canEdit={canEdit}
          canAssign={canEdit && users.length > 0}
          onAssign={(e: AlertEvent) => setAssignFor({ source: 'alert_event', id: e.id, title: alertTypeLabel[e.alert_type] || e.alert_type })}
          onActivity={(e: AlertEvent) => setActivityFor({ source: 'alert_event', id: e.id, title: alertTypeLabel[e.alert_type] || e.alert_type })}
          onRowClick={(e: AlertEvent) => setDetailFor({ source: 'alert_event', row: e, title: alertTypeLabel[e.alert_type] || e.alert_type })}
        />
      ) : tab === 'serviceErrors' ? (
        <ServiceErrorsPanel
          errors={serviceErrors}
          rollup={serviceRollup}
          filter={svcFilter}
          setFilter={setSvcFilter}
          onAcknowledge={handleServiceAck}
          onResolve={handleServiceResolve}
          onReopen={handleServiceReopen}
          canEdit={canEdit}
          canAssign={canEdit && users.length > 0}
          onAssign={(e: ServiceError) => setAssignFor({ source: 'service_error', id: e.id, title: `${e.service} · ${e.error_code}` })}
          onActivity={(e: ServiceError) => setActivityFor({ source: 'service_error', id: e.id, title: `${e.service} · ${e.error_code}` })}
          onRowClick={(e: ServiceError) => setDetailFor({ source: 'service_error', row: e, title: `${e.service} · ${e.error_code}` })}
        />
      ) : tab === 'emailConfig' ? (
        <EmailConfigPanel />
      ) : (
        <RulesPanel
          rules={rules}
          nodes={nodes}
          onUpdate={handleRuleUpdate}
          onSaveRules={handleRulesSave}
          onRefresh={fetchRules}
        />
      )}

      {assignFor && (
        <AssignModal
          title={assignFor.title}
          users={users}
          onClose={() => setAssignFor(null)}
          onConfirm={(u: CloudUser) => {
            handleAssign(assignFor, u);
            setAssignFor(null);
          }}
        />
      )}
      {activityFor && (
        <ActivityModal
          title={activityFor.title}
          source={activityFor.source}
          alertId={activityFor.id}
          onClose={() => setActivityFor(null)}
        />
      )}
      {detailFor && (
        <DetailModal
          source={detailFor.source}
          row={detailFor.row}
          title={detailFor.title}
          nodes={nodes}
          onClose={() => setDetailFor(null)}
        />
      )}
      {eventActionFor && (
        <NoteModal
          kind={eventActionFor.kind}
          onClose={() => setEventActionFor(null)}
          onConfirm={(text: string) => {
            if (eventActionFor.kind === 'resolve') handleResolveEvent(eventActionFor.id, text);
            else handleCancelEvent(eventActionFor.id, text);
            setEventActionFor(null);
          }}
        />
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
//  EVENTS PANEL
// ═══════════════════════════════════════════════════════════════

function EventsPanel({
  events, filter, setFilter, onAcknowledge, onResolve, onCancel, canEdit, canAssign, onAssign, onActivity, onRowClick,
}: {
  events: AlertEvent[];
  filter: { active?: boolean; severity?: string };
  setFilter: (f: any) => void;
  onAcknowledge: (id: number) => void;
  onResolve: (e: AlertEvent) => void;
  onCancel: (e: AlertEvent) => void;
  canEdit: boolean;
  canAssign: boolean;
  onAssign: (e: AlertEvent) => void;
  onActivity: (e: AlertEvent) => void;
  onRowClick: (e: AlertEvent) => void;
}) {
  return (
    <div className="space-y-4">
      {/* ── Filter Bar ── */}
      <div className={`${cardCls} p-4`}>
        <div className="flex flex-wrap items-center gap-3">
          <select
            value={filter.active === undefined ? 'all' : filter.active ? 'active' : 'resolved'}
            onChange={(e) => {
              const v = e.target.value;
              setFilter({ ...filter, active: v === 'all' ? undefined : v === 'active' });
            }}
            className={selectCls}
          >
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="resolved">Resolved</option>
          </select>
          <select
            value={filter.severity || ''}
            onChange={(e) => setFilter({ ...filter, severity: e.target.value || undefined })}
            className={selectCls}
          >
            <option value="">All Severities</option>
            <option value="critical">Critical</option>
            <option value="warning">Warning</option>
          </select>
          {(filter.active !== undefined || filter.severity) && (
            <button
              onClick={() => setFilter({})}
              className="text-[12px] text-muted-foreground hover:text-foreground transition-colors"
            >
              Clear all
            </button>
          )}
        </div>
      </div>

      {/* ── Events Table ── */}
      <div className={`${cardCls} overflow-hidden`}>
        <div className="overflow-x-auto">
          {events.length === 0 ? (
            <div className="py-16 text-center text-muted-foreground text-sm">
              No alert events found for the selected filters.
            </div>
          ) : (
            <table className="w-full text-[13px]">
              <thead>
                <tr className="border-b border-border bg-muted/50">
                  <th className={thCls}>Alert</th>
                  <th className={thCls}>Description</th>
                  <th className={thCls}>Severity</th>
                  <th className={thCls}>Node</th>
                  <th className={thCls}>Status</th>
                  <th className={thCls}>Assignee</th>
                  <th className={thCls}>Action</th>
                </tr>
              </thead>
              <tbody>
                {events.map((event) => (
                  <tr
                    key={event.id}
                    onClick={() => onRowClick(event)}
                    className={`border-b border-border last:border-0 hover:bg-muted/30 transition-colors cursor-pointer ${event.active ? '' : 'opacity-60'}`}
                  >
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span className="flex items-center gap-2">
                        <span className={`flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-lg ${sevColors[event.severity] || 'bg-gray-500/15 text-gray-500'}`}>
                          {alertTypeIcon[event.alert_type] || <BellAlertIcon className="h-4 w-4" />}
                        </span>
                        <span className="font-medium text-foreground">
                          {alertTypeLabel[event.alert_type] || event.alert_type}
                        </span>
                      </span>
                    </td>
                    <td className="px-4 py-3 max-w-[320px]">
                      <span className="block truncate text-muted-foreground" title={event.message || undefined}>
                        {event.message || `${event.node_name || event.node_id} — ${event.value?.toFixed(1)}% (threshold: ${event.threshold}%)`}
                      </span>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <SeverityBadge severity={event.severity} />
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-foreground">{event.node_name || event.node_id}</td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <StatusBadge active={event.active} resolvedAt={event.resolved_at} cancelled={event.cancelled} />
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-muted-foreground" title={event.assigned_by_name ? `Assigned by ${event.assigned_by_name}` : undefined}>
                      {event.assigned_to ? (event.assigned_to_name || event.assigned_to) : '—'}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap" onClick={(ev) => ev.stopPropagation()}>
                      <span className="flex items-center gap-1.5">
                        {canEdit && event.active && !event.acknowledged ? (
                          <button
                            onClick={() => onAcknowledge(event.id)}
                            title="Acknowledge this alert"
                            className="h-7 px-2.5 rounded-md bg-brand/10 text-brand text-[11px] font-medium hover:bg-brand/20 transition-colors"
                          >
                            Acknowledge
                          </button>
                        ) : event.acknowledged ? (
                          <span className="text-[11px] text-muted-foreground">✓ Acked by {event.acknowledged_by_name || event.acknowledged_by}</span>
                        ) : null}
                        {canAssign && event.active && (
                          <button
                            onClick={() => onAssign(event)}
                            title="Assign to a user"
                            className="flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
                          >
                            <UserPlusIcon className="h-4 w-4" />
                          </button>
                        )}
                        {canEdit && event.active && (
                          <>
                            <button
                              onClick={() => onResolve(event)}
                              title="Resolve this alert"
                              className="flex h-7 w-7 items-center justify-center rounded-md text-emerald-500 hover:bg-emerald-500/10 transition-colors"
                            >
                              <CheckCircleIcon className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => onCancel(event)}
                              title="Cancel (dismiss) this alert"
                              className="flex h-7 w-7 items-center justify-center rounded-md text-red-500 hover:bg-red-500/10 transition-colors"
                            >
                              <XMarkIcon className="h-4 w-4" />
                            </button>
                          </>
                        )}
                        <button
                          onClick={() => onActivity(event)}
                          title="Activity & comments"
                          className="flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
                        >
                          <ChatBubbleLeftRightIcon className="h-4 w-4" />
                        </button>
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
//  SERVICE ERRORS PANEL  (system/service domain — NOT AI analytics)
// ═══════════════════════════════════════════════════════════════

function ServiceErrorsPanel({
  errors, rollup, filter, setFilter, onAcknowledge, onResolve, onReopen, canEdit, canAssign, onAssign, onActivity, onRowClick,
}: {
  errors: ServiceError[];
  rollup: ServiceRollup[];
  filter: { active?: boolean; severity?: string; service?: string };
  setFilter: (f: any) => void;
  onAcknowledge: (id: number) => void;
  onResolve: (id: number) => void;
  onReopen: (id: number) => void;
  canEdit: boolean;
  canAssign: boolean;
  onAssign: (e: ServiceError) => void;
  onActivity: (e: ServiceError) => void;
  onRowClick: (e: ServiceError) => void;
}) {
  // Host name arrives inside the freeform error `details` payload (not a column).
  const hostOf = (e: ServiceError): string | null =>
    (e.details && (e.details.hostname || e.details.host)) || null;

  // Group the (already filtered) error list by service for display.
  const byService = errors.reduce<Record<string, ServiceError[]>>((acc, e) => {
    (acc[e.service] ||= []).push(e);
    return acc;
  }, {});
  const services = Object.keys(byService).sort();

  return (
    <div className="space-y-4">
      {/* ── Per-service summary grid (service-wise health) ── */}
      {rollup.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
          {rollup.map((s) => (
            <button
              key={s.service}
              onClick={() => setFilter({ ...filter, service: filter.service === s.service ? undefined : s.service })}
              className={`${cardCls} p-4 text-left transition-colors hover:bg-muted/30 ${
                filter.service === s.service ? 'ring-2 ring-brand' : ''
              } ${s.critical > 0 ? 'border-red-500/40' : ''}`}
            >
              <div className="flex items-center justify-between">
                <span className="text-[13px] font-medium text-foreground truncate">{s.service}</span>
                <span className={`h-2.5 w-2.5 rounded-full flex-shrink-0 ${s.critical > 0 ? 'bg-red-500 animate-pulse' : 'bg-amber-400'}`} />
              </div>
              <div className="mt-2 flex items-baseline gap-1.5">
                <span className="text-2xl font-semibold text-foreground">{s.active}</span>
                <span className="text-[11px] text-muted-foreground">active</span>
                {s.critical > 0 && (
                  <span className="ml-auto text-[11px] font-medium text-red-500">{s.critical} critical</span>
                )}
              </div>
              <p className="text-[11px] text-muted-foreground mt-1">
                last: {s.last_seen ? formatDateTime(s.last_seen) : '—'}
              </p>
            </button>
          ))}
        </div>
      )}

      {/* ── Filter Bar ── */}
      <div className={`${cardCls} p-4`}>
        <div className="flex flex-wrap items-center gap-3">
          <select
            value={filter.active === undefined ? 'all' : filter.active ? 'active' : 'resolved'}
            onChange={(e) => {
              const v = e.target.value;
              setFilter({ ...filter, active: v === 'all' ? undefined : v === 'active' });
            }}
            className={selectCls}
          >
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="resolved">Resolved</option>
          </select>
          <select
            value={filter.severity || ''}
            onChange={(e) => setFilter({ ...filter, severity: e.target.value || undefined })}
            className={selectCls}
          >
            <option value="">All Severities</option>
            <option value="critical">Critical</option>
            <option value="error">Error</option>
            <option value="warning">Warning</option>
          </select>
          {filter.service && (
            <button
              onClick={() => setFilter({ ...filter, service: undefined })}
              className="flex h-8 items-center gap-1 rounded-lg bg-brand/10 px-3 text-[12px] font-medium text-brand hover:bg-brand/20 transition-colors"
            >
              {filter.service}
              <XMarkIcon className="h-3.5 w-3.5" />
            </button>
          )}
          {(filter.active !== undefined || filter.severity || filter.service) && (
            <button
              onClick={() => setFilter({})}
              className="text-[12px] text-muted-foreground hover:text-foreground transition-colors"
            >
              Clear all
            </button>
          )}
        </div>
      </div>

      {/* ── Errors Table (grouped by service) ── */}
      <div className={`${cardCls} overflow-hidden`}>
        <div className="overflow-x-auto">
          {services.length === 0 ? (
            <div className="py-16 text-center text-muted-foreground text-sm">
              No service errors found for the selected filters.
            </div>
          ) : (
            <table className="w-full text-[13px]">
              <thead>
                <tr className="border-b border-border bg-muted/50">
                  <th className={thCls}>Error Code</th>
                  <th className={thCls}>Message</th>
                  <th className={thCls}>Severity</th>
                  <th className={thCls}>Component</th>
                  <th className={thCls}>Node</th>
                  <th className={thCls}>Count</th>
                  <th className={thCls}>Last Seen</th>
                  <th className={thCls}>Status</th>
                  <th className={thCls}>Assignee</th>
                  <th className={thCls}>Action</th>
                </tr>
              </thead>
              <tbody>
                {services.map((svc) => (
                  <React.Fragment key={svc}>
                    <tr className="border-b border-border bg-muted/40">
                      <td colSpan={10} className="px-4 py-2">
                        <span className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                          <ServerStackIcon className="h-3.5 w-3.5" />
                          {svc}
                          <span className="font-normal normal-case">({byService[svc].length})</span>
                        </span>
                      </td>
                    </tr>
                    {byService[svc].map((e) => (
                      <tr
                        key={e.id}
                        onClick={() => onRowClick(e)}
                        className={`border-b border-border last:border-0 hover:bg-muted/30 transition-colors cursor-pointer ${e.active ? '' : 'opacity-60'}`}
                      >
                        <td className="px-4 py-3 whitespace-nowrap">
                          <span className="font-mono text-[11px] px-1.5 py-0.5 rounded bg-muted text-foreground">{e.error_code}</span>
                        </td>
                        <td className="px-4 py-3 max-w-[320px]">
                          <span className="block truncate text-foreground" title={e.message}>{e.message}</span>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <SeverityBadge severity={e.severity} />
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-muted-foreground">{e.component || '—'}</td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <span className="block text-foreground">{e.node_name || e.node_id}</span>
                          {hostOf(e) && <span className="block text-[11px] text-muted-foreground">{hostOf(e)}</span>}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          {e.occurrence_count > 1 ? (
                            <span className={`${badgeCls} bg-muted text-foreground`}>×{e.occurrence_count}</span>
                          ) : (
                            <span className="text-muted-foreground">—</span>
                          )}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-muted-foreground" title={e.acknowledged ? `Acked by ${e.acknowledged_by_name || e.acknowledged_by}` : undefined}>
                          {formatDateTime(e.last_seen)}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          {e.active ? (
                            <span className={`${badgeCls} bg-red-500/15 text-red-500 animate-pulse`}>Active</span>
                          ) : (
                            <span className={`${badgeCls} bg-emerald-500/15 text-emerald-500`}>Resolved</span>
                          )}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-muted-foreground" title={e.assigned_by_name ? `Assigned by ${e.assigned_by_name}` : undefined}>
                          {e.assigned_to ? (e.assigned_to_name || e.assigned_to) : '—'}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap" onClick={(ev) => ev.stopPropagation()}>
                          <span className="flex items-center gap-1.5">
                            {canEdit && e.active && !e.acknowledged && (
                              <button
                                onClick={() => onAcknowledge(e.id)}
                                title="Acknowledge this error"
                                className="h-7 px-2.5 rounded-md bg-brand/10 text-brand text-[11px] font-medium hover:bg-brand/20 transition-colors"
                              >
                                Acknowledge
                              </button>
                            )}
                            {e.active && e.acknowledged && (
                              <span className="text-[11px] text-muted-foreground">✓ {e.acknowledged_by_name || e.acknowledged_by}</span>
                            )}
                            {canEdit && e.active && (
                              <button
                                onClick={() => onResolve(e.id)}
                                title="Resolve this error"
                                className="h-7 px-2.5 rounded-md bg-emerald-500/10 text-emerald-500 text-[11px] font-medium hover:bg-emerald-500/20 transition-colors"
                              >
                                Resolve
                              </button>
                            )}
                            {!e.active && (
                              <>
                                {e.resolved_by_name && (
                                  <span className="text-[11px] text-muted-foreground">✓ Resolved by {e.resolved_by_name}</span>
                                )}
                                {canEdit && <button
                                  onClick={() => onReopen(e.id)}
                                  title="Reopen this error"
                                  className="flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
                                >
                                  <ArrowUturnLeftIcon className="h-4 w-4" />
                                </button>}
                              </>
                            )}
                            {canAssign && e.active && (
                              <button
                                onClick={() => onAssign(e)}
                                title="Assign to a user"
                                className="flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
                              >
                                <UserPlusIcon className="h-4 w-4" />
                              </button>
                            )}
                            <button
                              onClick={() => onActivity(e)}
                              title="Activity & comments"
                              className="flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
                            >
                              <ChatBubbleLeftRightIcon className="h-4 w-4" />
                            </button>
                          </span>
                        </td>
                      </tr>
                    ))}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
//  RULES PANEL
// ═══════════════════════════════════════════════════════════════

const RULE_TYPES = ['cpu_high', 'memory_high', 'disk_high', 'node_offline'] as const;
type RuleDraft = Record<string, { enabled: boolean; warning_threshold: number; critical_threshold: number; consecutive_checks: number; cooldown_minutes: number }>;

function RulesPanel({
  rules, nodes, onUpdate, onSaveRules, onRefresh,
}: {
  rules: AlertRule[];
  nodes: any[];
  onUpdate: (rule: AlertRule, field: string, value: any) => void;
  onSaveRules: (nodeId: string, rulesArr: any[]) => Promise<void>;
  onRefresh: () => void;
}) {
  const [editingNode, setEditingNode] = useState<string>('*');
  const [emailInput, setEmailInput] = useState('');
  const [confirming, setConfirming] = useState(false);
  const [saving, setSaving] = useState(false);

  // Group rules by node_id
  const nodeIds = Array.from(new Set(rules.map(r => r.node_id)));
  if (!nodeIds.includes('*')) nodeIds.unshift('*');

  const filteredRules = rules.filter(r => r.node_id === editingNode);

  // Baseline (saved) values for the selected node, per alert type — the global default
  // fills in for a type the node hasn't overridden yet.
  const buildDraft = useCallback((): RuleDraft => {
    const d: RuleDraft = {};
    for (const type of RULE_TYPES) {
      const eff = rules.find(r => r.node_id === editingNode && r.alert_type === type)
        || rules.find(r => r.node_id === '*' && r.alert_type === type);
      if (eff) {
        d[type] = {
          enabled: eff.enabled,
          warning_threshold: eff.warning_threshold,
          critical_threshold: eff.critical_threshold,
          consecutive_checks: eff.consecutive_checks,
          cooldown_minutes: eff.cooldown_minutes,
        };
      }
    }
    return d;
  }, [rules, editingNode]);

  // Draft holds unsaved edits; rebuilt whenever the node or the loaded rules change.
  const [draft, setDraft] = useState<RuleDraft>(buildDraft);
  useEffect(() => { setDraft(buildDraft()); }, [buildDraft]);

  const baseline = buildDraft();
  const dirty = JSON.stringify(draft) !== JSON.stringify(baseline);

  const setField = (type: string, field: string, value: any) =>
    setDraft(prev => ({ ...prev, [type]: { ...prev[type], [field]: value } }));

  const applyChanges = async () => {
    setSaving(true);
    try {
      const rulesArr = RULE_TYPES.filter(t => draft[t]).map(t => ({ alert_type: t, ...draft[t] }));
      await onSaveRules(editingNode, rulesArr);
    } catch { /* ignore */ }
    finally { setSaving(false); setConfirming(false); }
  };

  const getNodeName = (nodeId: string) => {
    if (nodeId === '*') return 'Global Defaults';
    if (nodeId === 'cloud-self') return 'Cloud Server';
    const node = nodes.find((n: any) => n.nodeId === nodeId);
    return node?.name || nodeId.substring(0, 12) + '...';
  };

  const addEmailRecipient = async (rule: AlertRule) => {
    if (!emailInput.trim() || !emailInput.includes('@')) return;
    const newRecipients = [...(rule.email_recipients || []), emailInput.trim()];
    await onUpdate(rule, 'email_recipients', newRecipients);
    setEmailInput('');
    onRefresh();
  };

  const removeEmailRecipient = async (rule: AlertRule, email: string) => {
    const newRecipients = (rule.email_recipients || []).filter(e => e !== email);
    await onUpdate(rule, 'email_recipients', newRecipients);
    onRefresh();
  };

  const numInputCls = 'w-16 h-8 rounded-lg border border-border bg-background text-center text-[13px] text-foreground focus:border-brand focus:outline-none';

  return (
    <div className="space-y-4">
      {/* ── Node selector ── */}
      <div className={`${cardCls} p-4`}>
        <div className="flex flex-wrap items-center gap-3">
          <label className="text-[13px] text-muted-foreground">Configure for:</label>
          <select
            value={editingNode}
            onChange={(e) => setEditingNode(e.target.value)}
            className={selectCls}
          >
            {nodeIds.map(id => (
              <option key={id} value={id}>{getNodeName(id)}</option>
            ))}
            {/* Also show nodes not yet in rules */}
            {nodes.filter((n: any) => !nodeIds.includes(n.nodeId)).map((n: any) => (
              <option key={n.nodeId} value={n.nodeId}>{n.name}</option>
            ))}
            {!nodeIds.includes('cloud-self') && (
              <option value="cloud-self">Cloud Server</option>
            )}
          </select>
        </div>
      </div>

      {/* ── Rules table ── */}
      <div className={`${cardCls} overflow-hidden`}>
        <div className="overflow-x-auto">
          <table className="w-full text-[13px]">
            <thead>
              <tr className="border-b border-border bg-muted/50">
                <th className={thCls}>Alert Type</th>
                <th className={`${thCls} text-center`}>Enabled</th>
                <th className={`${thCls} text-center`}>Warning %</th>
                <th className={`${thCls} text-center`}>Critical %</th>
                <th className={`${thCls} text-center`}>Checks</th>
                <th className={`${thCls} text-center`}>Cooldown (min)</th>
              </tr>
            </thead>
            <tbody>
              {RULE_TYPES.map(type => {
                const d = draft[type];
                if (!d) return null;
                const hasOwn = filteredRules.some(r => r.alert_type === type);
                return (
                  <tr key={type} className="border-b border-border last:border-0 hover:bg-muted/30 transition-colors">
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span className="flex items-center gap-2">
                        <span className="text-muted-foreground">{alertTypeIcon[type]}</span>
                        <span className="font-medium text-foreground">{alertTypeLabel[type]}</span>
                        {!hasOwn && editingNode !== '*' && (
                          <span className="text-[11px] text-muted-foreground italic">(global default)</span>
                        )}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <button
                        onClick={() => setField(type, 'enabled', !d.enabled)}
                        className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${d.enabled ? 'bg-brand' : 'bg-muted'}`}
                      >
                        <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow transition-transform ${d.enabled ? 'translate-x-[18px]' : 'translate-x-1'}`} />
                      </button>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <input type="number" min={1} max={100} value={d.warning_threshold}
                        onChange={(e) => setField(type, 'warning_threshold', parseInt(e.target.value))} className={numInputCls} />
                    </td>
                    <td className="px-4 py-3 text-center">
                      <input type="number" min={1} max={100} value={d.critical_threshold}
                        onChange={(e) => setField(type, 'critical_threshold', parseInt(e.target.value))} className={numInputCls} />
                    </td>
                    <td className="px-4 py-3 text-center">
                      <input type="number" min={1} max={20} value={d.consecutive_checks}
                        onChange={(e) => setField(type, 'consecutive_checks', parseInt(e.target.value))} className={numInputCls} />
                    </td>
                    <td className="px-4 py-3 text-center">
                      <input type="number" min={1} max={1440} value={d.cooldown_minutes}
                        onChange={(e) => setField(type, 'cooldown_minutes', parseInt(e.target.value))} className={numInputCls} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── Save bar — only shown once a rule value changed; applies on confirm ── */}
      {dirty && (
        <div className={`${cardCls} p-3 flex items-center justify-between gap-3`}>
          <span className="text-[13px] text-amber-500">Unsaved rule changes for {getNodeName(editingNode)}.</span>
          <div className="flex items-center gap-2">
            <button onClick={() => setDraft(buildDraft())} disabled={saving} className={ghostBtnCls}>Discard</button>
            <button onClick={() => setConfirming(true)} disabled={saving} className={primaryBtnCls}>
              {saving ? 'Saving…' : 'Save changes'}
            </button>
          </div>
        </div>
      )}

      {confirming && (
        <ConfirmModal
          title="Apply rule changes?"
          message={`Save and apply the alert-rule changes for ${getNodeName(editingNode)}? They take effect on the next evaluation cycle.`}
          onCancel={() => setConfirming(false)}
          onConfirm={applyChanges}
        />
      )}

      {/* ── Email Recipients for selected node ── */}
      <div className={`${cardCls} p-5`}>
        <h3 className="text-[13px] font-semibold text-foreground mb-1">
          Email Recipients — {getNodeName(editingNode)}
        </h3>
        <p className="text-[12px] text-muted-foreground mb-3">
          Override recipients for this node's alerts. Leave empty to use global alert config recipients.
        </p>

        {filteredRules.length > 0 && filteredRules[0].email_recipients?.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-3">
            {filteredRules[0].email_recipients.map(email => (
              <span key={email} className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg border border-border bg-muted/50 text-[12px] text-foreground">
                {email}
                <button
                  onClick={() => removeEmailRecipient(filteredRules[0], email)}
                  className="text-muted-foreground hover:text-red-500 transition-colors"
                >
                  <XMarkIcon className="h-3.5 w-3.5" />
                </button>
              </span>
            ))}
          </div>
        )}

        <div className="flex gap-2">
          <input
            type="email"
            value={emailInput}
            onChange={(e) => setEmailInput(e.target.value)}
            placeholder="Add email recipient..."
            className={fieldCls}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && filteredRules[0]) addEmailRecipient(filteredRules[0]);
            }}
          />
          <button
            onClick={() => filteredRules[0] && addEmailRecipient(filteredRules[0])}
            className={primaryBtnCls}
          >
            Add
          </button>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
//  EMAIL CONFIG PANEL
// ═══════════════════════════════════════════════════════════════

interface EmailConfig {
  enabled: boolean;
  smtp_host: string;
  smtp_port: number;
  smtp_secure: boolean;
  smtp_user: string;
  smtp_pass: string;
  from_email: string;
  from_name: string;
  recipients: string[];
  cooldown_minutes: number;
  offline_threshold_seconds: number;
  resend_api_key: string;
}

interface EmailAlertLog {
  id: number;
  node_name: string;
  alert_type: string;
  recipients: string[];
  success: boolean;
  error_message: string | null;
  sent_at: string;
}

const defaultEmailConfig: EmailConfig = {
  enabled: false,
  smtp_host: '',
  smtp_port: 587,
  smtp_secure: false,
  smtp_user: '',
  smtp_pass: '',
  from_email: '',
  from_name: 'TRINETRA Alert System',
  recipients: [],
  cooldown_minutes: 15,
  offline_threshold_seconds: 120,
  resend_api_key: '',
};

function EmailConfigPanel() {
  const [config, setConfig] = useState<EmailConfig>(defaultEmailConfig);
  const [logs, setLogs] = useState<EmailAlertLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [recipientInput, setRecipientInput] = useState('');

  const fetchConfig = useCallback(async () => {
    try {
      const { data } = await (api as any).get('/alerts/config');
      if (data.success && data.data) {
        setConfig({ ...defaultEmailConfig, ...data.data, recipients: Array.isArray(data.data.recipients) ? data.data.recipients : [] });
      }
    } catch { /* ignore */ }
  }, []);

  const fetchLogs = useCallback(async () => {
    try {
      const { data } = await (api as any).get('/alerts/log?limit=30');
      if (data.success) setLogs(data.data || []);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    Promise.all([fetchConfig(), fetchLogs()]).finally(() => setLoading(false));
  }, [fetchConfig, fetchLogs]);

  const showMsg = (type: 'success' | 'error', text: string) => {
    setMessage({ type, text });
    setTimeout(() => setMessage(null), 5000);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const { data } = await (api as any).put('/alerts/config', config);
      if (data.success) {
        setConfig({ ...defaultEmailConfig, ...data.data, recipients: data.data.recipients || [] });
        showMsg('success', 'Email configuration saved successfully');
      } else {
        showMsg('error', data.error || 'Save failed');
      }
    } catch (err: any) {
      showMsg('error', err.message || 'Failed to save');
    } finally { setSaving(false); }
  };

  const handleTestEmail = async () => {
    if (!testEmail) { showMsg('error', 'Enter a test email address'); return; }
    setTesting(true);
    try {
      const { data } = await (api as any).post('/alerts/test', { email: testEmail });
      if (data.success) { showMsg('success', data.message); fetchLogs(); }
      else showMsg('error', data.error || 'Test failed');
    } catch (err: any) {
      showMsg('error', err.message || 'Failed to send test email');
    } finally { setTesting(false); }
  };

  const addRecipient = () => {
    const email = recipientInput.trim();
    if (email && !config.recipients.includes(email)) {
      setConfig((c: EmailConfig) => ({ ...c, recipients: [...c.recipients, email] }));
      setRecipientInput('');
    }
  };

  const removeRecipient = (email: string) => {
    setConfig((c: EmailConfig) => ({ ...c, recipients: c.recipients.filter((r: string) => r !== email) }));
  };

  if (loading) {
    return (
      <div className="space-y-3">
        {[1, 2, 3].map((i) => (
          <div key={i} className="h-24 rounded-xl bg-muted animate-pulse" />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* ── Enable + Status ── */}
      <div className={`${cardCls} p-5`}>
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-[15px] font-semibold text-foreground">Email Alert System</h2>
            <p className="text-[12px] text-muted-foreground mt-0.5">Configure email delivery for system alerts</p>
          </div>
          <label className="flex items-center gap-3 cursor-pointer">
            <span className="text-[13px] font-medium text-foreground">
              {config.enabled ? 'Enabled' : 'Disabled'}
            </span>
            <button
              onClick={() => setConfig((c: EmailConfig) => ({ ...c, enabled: !c.enabled }))}
              className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${config.enabled ? 'bg-brand' : 'bg-muted'}`}
            >
              <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow transition-transform ${config.enabled ? 'translate-x-[18px]' : 'translate-x-1'}`} />
            </button>
          </label>
        </div>
      </div>

      {message && (
        <div className={`px-4 py-3 rounded-lg text-[13px] font-medium border ${message.type === 'success' ? 'bg-emerald-500/10 text-emerald-500 border-emerald-500/30' : 'bg-red-500/10 text-red-500 border-red-500/30'}`}>
          {message.text}
        </div>
      )}

      {/* ── Email Provider ── */}
      <div className={`${cardCls} p-5`}>
        <h2 className="text-[15px] font-semibold text-foreground mb-1">Email Provider</h2>
        <p className="text-[12px] text-muted-foreground mb-4">
          Use <strong>Resend</strong> (recommended — works on all servers) or SMTP. If Resend API key is set, SMTP is ignored.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className={labelCls}>Resend API Key</label>
            <input className={fieldCls} type="password" placeholder="re_xxxxxxxxx" value={config.resend_api_key}
              onChange={e => setConfig(c => ({ ...c, resend_api_key: e.target.value }))} />
            <p className="text-[11px] text-muted-foreground mt-1">
              Get one free at <a href="https://resend.com" target="_blank" rel="noreferrer" className="text-brand hover:underline">resend.com</a> (100 emails/day free)
            </p>
          </div>
          <div>
            <label className={labelCls}>From Email</label>
            <input className={fieldCls} placeholder="alerts@yourdomain.com" value={config.from_email}
              onChange={e => setConfig(c => ({ ...c, from_email: e.target.value }))} />
            <p className="text-[11px] text-muted-foreground mt-1">
              For Resend: use <code className="text-[11px]">onboarding@resend.dev</code> to test
            </p>
          </div>
          <div>
            <label className={labelCls}>From Name</label>
            <input className={fieldCls} placeholder="TRINETRA Alert System" value={config.from_name}
              onChange={e => setConfig(c => ({ ...c, from_name: e.target.value }))} />
          </div>
        </div>
      </div>

      {/* ── SMTP ── */}
      <div className={`${cardCls} p-5`}>
        <h2 className="text-[15px] font-semibold text-foreground mb-4">
          SMTP Settings <span className="text-[12px] font-normal text-muted-foreground">(used only if Resend key is empty)</span>
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className={labelCls}>SMTP Host</label>
            <input className={fieldCls} placeholder="smtp.gmail.com" value={config.smtp_host}
              onChange={e => setConfig(c => ({ ...c, smtp_host: e.target.value }))} />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Port</label>
              <input className={fieldCls} type="number" value={config.smtp_port}
                onChange={e => setConfig(c => ({ ...c, smtp_port: parseInt(e.target.value) || 587 }))} />
            </div>
            <div>
              <label className={labelCls}>Secure (TLS)</label>
              <div className="flex items-center h-9">
                <input type="checkbox" className="w-4 h-4 rounded accent-[var(--brand)]" checked={config.smtp_secure}
                  onChange={e => setConfig(c => ({ ...c, smtp_secure: e.target.checked }))} />
                <span className="ml-2 text-[13px] text-muted-foreground">SSL/TLS</span>
              </div>
            </div>
          </div>
          <div>
            <label className={labelCls}>SMTP Username</label>
            <input className={fieldCls} placeholder="user@gmail.com" value={config.smtp_user}
              onChange={e => setConfig(c => ({ ...c, smtp_user: e.target.value }))} />
          </div>
          <div>
            <label className={labelCls}>SMTP Password</label>
            <input className={fieldCls} type="password" placeholder="App password" value={config.smtp_pass}
              onChange={e => setConfig(c => ({ ...c, smtp_pass: e.target.value }))} />
          </div>
        </div>
      </div>

      {/* ── Recipients ── */}
      <div className={`${cardCls} p-5`}>
        <h2 className="text-[15px] font-semibold text-foreground mb-1">Default Alert Recipients</h2>
        <p className="text-[12px] text-muted-foreground mb-3">These recipients receive all alerts unless overridden in Alert Rules per-node config.</p>
        <div className="flex gap-2 mb-3">
          <input className={fieldCls} placeholder="email@example.com" value={recipientInput}
            onChange={e => setRecipientInput(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addRecipient())} />
          <button onClick={addRecipient} className={primaryBtnCls}>
            Add
          </button>
        </div>
        {config.recipients.length === 0 ? (
          <p className="text-[13px] text-muted-foreground">No recipients configured — alerts will not be emailed</p>
        ) : (
          <div className="flex flex-wrap gap-2">
            {config.recipients.map(email => (
              <span key={email} className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[12px] bg-brand/10 text-brand border border-brand/30">
                {email}
                <button onClick={() => removeRecipient(email)} className="hover:text-red-500 transition-colors">
                  <XMarkIcon className="h-3.5 w-3.5" />
                </button>
              </span>
            ))}
          </div>
        )}
      </div>

      {/* ── Save + Test ── */}
      <div className={`${cardCls} p-5`}>
        <div className="flex flex-col md:flex-row items-start md:items-end gap-4">
          <div className="flex-1 w-full">
            <label className={labelCls}>Test Email Address</label>
            <input className={fieldCls} placeholder="your@email.com" value={testEmail}
              onChange={e => setTestEmail(e.target.value)} />
          </div>
          <button onClick={handleTestEmail} disabled={testing} className={ghostBtnCls}>
            {testing ? 'Sending…' : 'Send Test Email'}
          </button>
          <button onClick={handleSave} disabled={saving} className={primaryBtnCls}>
            {saving ? 'Saving…' : 'Save Configuration'}
          </button>
        </div>
      </div>

      {/* ── Alert Log ── */}
      <div className={`${cardCls} overflow-hidden`}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-border">
          <h2 className="text-[15px] font-semibold text-foreground">Email Delivery Log</h2>
          <button
            onClick={fetchLogs}
            className="flex h-8 items-center gap-1.5 rounded-lg border border-border px-3 text-[12px] font-medium text-foreground hover:bg-muted transition-colors"
          >
            <ArrowPathIcon className="h-3.5 w-3.5" /> Refresh
          </button>
        </div>
        {logs.length === 0 ? (
          <p className="py-12 text-center text-muted-foreground text-sm">No alerts sent yet</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-[13px]">
              <thead>
                <tr className="border-b border-border bg-muted/50">
                  <th className={thCls}>Time</th>
                  <th className={thCls}>Node</th>
                  <th className={thCls}>Type</th>
                  <th className={thCls}>Recipients</th>
                  <th className={thCls}>Status</th>
                </tr>
              </thead>
              <tbody>
                {logs.map(log => (
                  <tr key={log.id} className="border-b border-border last:border-0 hover:bg-muted/30 transition-colors">
                    <td className="px-4 py-3 whitespace-nowrap text-muted-foreground">{formatDateTime(log.sent_at)}</td>
                    <td className="px-4 py-3 whitespace-nowrap text-foreground">{log.node_name || '—'}</td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span className={`${badgeCls} ${log.alert_type === 'test' ? 'bg-blue-500/15 text-blue-500' : 'bg-red-500/15 text-red-500'}`}>
                        {log.alert_type}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-muted-foreground text-[12px] max-w-[320px]">
                      <span className="block truncate" title={Array.isArray(log.recipients) ? log.recipients.join(', ') : String(log.recipients)}>
                        {Array.isArray(log.recipients) ? log.recipients.join(', ') : String(log.recipients)}
                      </span>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      {log.success
                        ? <span className="text-emerald-500 text-[12px] font-medium">✓ Sent</span>
                        : <span className="text-red-500 text-[12px] font-medium" title={log.error_message || ''}>✗ Failed</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
//  ASSIGN + ACTIVITY MODALS (shared by both system-alert domains)
// ═══════════════════════════════════════════════════════════════

function AssignModal({
  title, users, onClose, onConfirm,
}: {
  title: string;
  users: CloudUser[];
  onClose: () => void;
  onConfirm: (u: CloudUser) => void;
}) {
  const [assigneeId, setAssigneeId] = useState(users[0]?.id || '');
  const assignee = users.find((u) => u.id === assigneeId);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" onClick={onClose}>
      <div className="w-full max-w-md rounded-2xl border border-border bg-card shadow-2xl p-6" onClick={(e) => e.stopPropagation()}>
        <h2 className="text-lg font-semibold text-foreground mb-1">Assign alert</h2>
        <p className="text-[12px] text-muted-foreground mb-3 truncate">{title}</p>
        <select value={assigneeId} onChange={(e) => setAssigneeId(e.target.value)} className={`${selectCls} w-full`}>
          {users.map((u) => (
            <option key={u.id} value={u.id}>{u.name || u.email}</option>
          ))}
        </select>
        <div className="flex justify-end gap-2 mt-4">
          <button onClick={onClose} className={ghostBtnCls}>Cancel</button>
          <button onClick={() => assignee && onConfirm(assignee)} disabled={!assignee} className={primaryBtnCls}>
            Assign
          </button>
        </div>
      </div>
    </div>
  );
}

interface SystemAlertAction {
  id: number;
  action: string;
  actor_id: string | null;
  actor_name: string | null;
  target_id: string | null;
  target_name: string | null;
  note: string | null;
  created_at: string;
}

const ACTION_LABEL: Record<string, string> = {
  acknowledged: 'acknowledged this alert',
  assigned: 'assigned this alert',
  resolved: 'resolved this alert',
  cancelled: 'cancelled this alert',
  reopened: 'reopened this alert',
  commented: 'commented',
};

/** The lifecycle activity list + comment box for one alert/error. Shared by the
 *  Activity modal and the row-detail modal. */
function ActivityFeed({ source, alertId }: { source: AlertSource; alertId: number }) {
  const [actions, setActions] = useState<SystemAlertAction[]>([]);
  const [loading, setLoading] = useState(true);
  const [comment, setComment] = useState('');
  const [posting, setPosting] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await api.get(`${SOURCE_BASE[source]}/${alertId}/actions`);
      setActions(res.data?.data || res.data || []);
    } catch {
      setActions([]);
    } finally {
      setLoading(false);
    }
  }, [source, alertId]);

  useEffect(() => { load(); }, [load]);

  const nameOf = (id: string | null, name: string | null) => name || id || 'Unknown';

  const postComment = async () => {
    const text = comment.trim();
    if (!text || posting) return;
    setPosting(true);
    try {
      await api.post(`${SOURCE_BASE[source]}/${alertId}/comments`, { comment: text });
      setComment('');
      await load();
    } catch { /* ignore */ }
    finally { setPosting(false); }
  };

  return (
    <>
      <h3 className="text-[12px] font-semibold uppercase tracking-wider text-muted-foreground mb-2">Lifecycle log</h3>
      {loading ? (
        <p className="text-[12px] text-muted-foreground">Loading activity…</p>
      ) : actions.length === 0 ? (
        <p className="text-[12px] text-muted-foreground">No activity yet.</p>
      ) : (
        <ul className="space-y-2 max-h-72 overflow-y-auto pr-1">
          {actions.map((ac) => (
            <li key={ac.id} className="text-[13px]">
              <div className="flex items-baseline justify-between gap-2">
                <span className="text-foreground">
                  <span className="font-medium">{nameOf(ac.actor_id, ac.actor_name)}</span>
                  {' '}{ACTION_LABEL[ac.action] || ac.action}
                  {ac.action === 'assigned' && (
                    <> to <span className="font-medium">{nameOf(ac.target_id, ac.target_name)}</span></>
                  )}
                </span>
                <span className="text-[11px] text-muted-foreground whitespace-nowrap">{formatDateTime(ac.created_at)}</span>
              </div>
              {ac.note && (
                <p className="mt-0.5 rounded bg-muted/60 px-2 py-1 text-[12px] text-foreground whitespace-pre-wrap">{ac.note}</p>
              )}
            </li>
          ))}
        </ul>
      )}

      <div className="flex gap-2 mt-4">
        <input
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && postComment()}
          placeholder="Add a comment…"
          className={fieldCls}
        />
        <button onClick={postComment} disabled={!comment.trim() || posting} className={primaryBtnCls}>
          {posting ? 'Posting…' : 'Comment'}
        </button>
      </div>
    </>
  );
}

function ActivityModal({
  title, source, alertId, onClose,
}: {
  title: string;
  source: AlertSource;
  alertId: number;
  onClose: () => void;
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" onClick={onClose}>
      <div className="w-full max-w-lg max-h-[80vh] overflow-y-auto rounded-2xl border border-border bg-card shadow-2xl p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-1">
          <h2 className="text-lg font-semibold text-foreground">Activity</h2>
          <button onClick={onClose} className="flex h-8 w-8 items-center justify-center rounded-lg border border-border text-muted-foreground hover:bg-muted transition-colors">
            <XMarkIcon className="h-4 w-4" />
          </button>
        </div>
        <p className="text-[12px] text-muted-foreground mb-3 truncate">{title}</p>
        <ActivityFeed source={source} alertId={alertId} />
      </div>
    </div>
  );
}

// ─── Row-detail modal (full record + lifecycle log) ─────────────
function DField({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-0.5">
      <dt className="text-[11px] font-medium text-muted-foreground uppercase tracking-wide">{label}</dt>
      <dd className="text-[13px] text-foreground font-medium break-words">{value ?? '—'}</dd>
    </div>
  );
}

function DetailModal({
  source, row, title, nodes, onClose,
}: {
  source: AlertSource;
  row: any;
  title: string;
  nodes: any[];
  onClose: () => void;
}) {
  const nodeName = row.node_name || nodes.find((n: any) => n.nodeId === row.node_id)?.name || row.node_id;
  const host = row.details && (row.details.hostname || row.details.host);
  const assignee = row.assigned_to ? (row.assigned_to_name || row.assigned_to) : 'Unassigned';
  const isEvent = source === 'alert_event';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" onClick={onClose}>
      <div className="w-full max-w-2xl max-h-[88vh] overflow-y-auto rounded-2xl border border-border bg-card shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 z-10 flex items-center justify-between border-b border-border bg-card/95 backdrop-blur px-6 py-4 rounded-t-2xl">
          <div className="flex items-center gap-2 min-w-0">
            <h2 className="text-lg font-semibold text-foreground truncate">{title}</h2>
            <SeverityBadge severity={row.severity} />
            <StatusBadge active={row.active} resolvedAt={row.resolved_at ?? undefined} cancelled={row.cancelled} />
          </div>
          <button onClick={onClose} className="flex h-8 w-8 items-center justify-center rounded-lg border border-border text-muted-foreground hover:bg-muted transition-colors">
            <XMarkIcon className="h-4 w-4" />
          </button>
        </div>

        <div className="px-6 py-4">
          {row.message && <p className="text-[13px] text-foreground mb-4">{row.message}</p>}
          <h3 className="text-[12px] font-semibold uppercase tracking-wider text-muted-foreground mb-3">Details</h3>
          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 mb-5">
            <DField label="Node" value={host ? `${nodeName} · ${host}` : nodeName} />
            {isEvent ? (
              <>
                <DField label="Alert type" value={alertTypeLabel[row.alert_type] || row.alert_type} />
                <DField label="Value / threshold" value={row.value != null ? `${Number(row.value).toFixed(1)}% / ${row.threshold}%` : '—'} />
                <DField label="Detected" value={formatDateTime(row.created_at)} />
              </>
            ) : (
              <>
                <DField label="Error code" value={<span className="font-mono">{row.error_code}</span>} />
                <DField label="Service / component" value={[row.service, row.component].filter(Boolean).join(' · ') || '—'} />
                <DField label="Occurrences" value={row.occurrence_count} />
                <DField label="First seen" value={formatDateTime(row.created_at)} />
                <DField label="Last seen" value={formatDateTime(row.last_seen)} />
              </>
            )}
            <DField label="Acknowledged by" value={row.acknowledged ? (row.acknowledged_by_name || row.acknowledged_by || 'Yes') : '—'} />
            <DField label="Assignee" value={assignee} />
            {row.resolved_by_name && <DField label="Resolved by" value={row.resolved_by_name} />}
            {row.cancelled && <DField label="Cancelled by" value={row.cancelled_by_name || 'Yes'} />}
            {row.cancel_reason && <DField label="Cancel reason" value={row.cancel_reason} />}
          </dl>

          <div className="border-t border-border pt-4">
            <ActivityFeed source={source} alertId={row.id} />
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Small prompt for event resolve/cancel note ─────────────────
function NoteModal({ kind, onClose, onConfirm }: { kind: 'resolve' | 'cancel'; onClose: () => void; onConfirm: (text: string) => void }) {
  const [text, setText] = useState('');
  const title = kind === 'resolve' ? 'Resolve alert' : 'Cancel (dismiss) alert';
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" onClick={onClose}>
      <div className="w-full max-w-md rounded-2xl border border-border bg-card shadow-2xl p-6" onClick={(e) => e.stopPropagation()}>
        <h2 className="text-lg font-semibold text-foreground mb-3">{title}</h2>
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={3}
          placeholder={kind === 'resolve' ? 'Resolution note (optional)' : 'Reason (optional)'}
          className="w-full rounded-lg border border-border bg-background px-3 py-2 text-[13px] text-foreground placeholder:text-muted-foreground focus:border-brand focus:outline-none"
          autoFocus
        />
        <div className="flex justify-end gap-2 mt-4">
          <button onClick={onClose} className={ghostBtnCls}>Cancel</button>
          <button onClick={() => onConfirm(text.trim())} className={primaryBtnCls}>Confirm</button>
        </div>
      </div>
    </div>
  );
}

// ─── Confirm dialog for applying rule changes ───────────────────
function ConfirmModal({ title, message, onCancel, onConfirm }: { title: string; message: string; onCancel: () => void; onConfirm: () => void }) {
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" onClick={onCancel}>
      <div className="w-full max-w-md rounded-2xl border border-border bg-card shadow-2xl p-6" onClick={(e) => e.stopPropagation()}>
        <h2 className="text-lg font-semibold text-foreground mb-2">{title}</h2>
        <p className="text-[13px] text-muted-foreground mb-4">{message}</p>
        <div className="flex justify-end gap-2">
          <button onClick={onCancel} className={ghostBtnCls}>Cancel</button>
          <button onClick={onConfirm} className={primaryBtnCls}>Apply changes</button>
        </div>
      </div>
    </div>
  );
}
